import { EMPTY_ARRAY } from 'constants/defaults';
import type { OnboardingHint } from 'constants/hints/onboarding';
import {
  HAS_IGNORED_MEMORY,
  HAS_SEEN_AI_IGNORED_MEMORIES,
  HAS_SEEN_UNLINKED_MEMORIES,
} from 'constants/hints/onboarding';
import {
  HAS_SEEN_AI_IGNORED_MEMORIES_HINT,
  HIDE_SHORT_DURATION_MEMORY_TITLES,
} from 'constants/user';
import {
  combinedDurationOfMemories,
  entryToMemoryFactory,
  generateMemoryTimelineFactory,
  groupMemoriesFactory,
  memoryEmptyStateFactory,
  splitMemoryGroupsByStatus,
} from 'factories/timeline';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { getUseTicAPI } from 'redux/modules/tic/tic.auth.selectors';
import { getTicViewDemoMode } from 'redux/modules/tic/tic.demo.selectors';
import type {
  RelevantMemoryFilters,
  RelevantMetaMemoryFilters,
} from 'redux/modules/timeline.types';
import {
  getAccountFeatures,
  getHasTicWorkspaceSupport,
  getIsSubscriptionExpiredOrTrialEnded,
} from 'redux/selectors/accounts';
import { getHasConnectedMemorySource, getToolbarApps } from 'redux/selectors/apps';
import { getFilter as getCalendarFilter } from 'redux/selectors/calendar.filter';
import { getHints } from 'redux/selectors/hints';
import { getMemoryToProjectRelationsForCalendarDay } from 'redux/selectors/hours';
import {
  getEntryIdsAssociatedToHoursOnDraftDay,
  getEntryIdsAssociatedToSuggestionsOnDraftDay,
  getHourDraftProjectMeta,
  getHourDraftStatus,
} from 'redux/selectors/hours.draft';
import {
  getHourDraftDay,
  getHourDraftEntryIds,
  getHourDraftProjectId,
  getHourDraftScrollToTimestamp,
  getHourDraftTimestamps,
} from 'redux/selectors/hours.draft.base';
import {
  getAiIgnoredMemories,
  getFilter,
  getLastPusherUpdate,
  getMemoryGroups,
  getMemoryGroupsWithProjects,
  getProjectPredictions,
  getScrollPosition,
  getTimelineEntities,
  getTimelineEntries,
  getTimelineResult,
  getTimelineSelection,
  getTimelineStyle,
  getTimelineXhrStatusState,
  getTimestampSnappingMinutes,
} from 'redux/selectors/timeline';
import {
  getHasEnabledTic,
  getIsUserInExperimentalFeatureGroup,
  getUserPropertyAsBoolean,
  getUserPropertyAsNumber,
} from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type {
  DisplayRules,
  ExtractTimelineMetaFromEntries,
  MemoriesGroupedByStatus,
  Memory,
  MemoryGroup,
  SuggestedEntry,
} from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import type { Duration } from 'types/units';
import { isSameDay } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { lazySelector } from 'utils/reselect';
import { datetimeTools } from 'utils/time';
import { createTimelineMetadata, filterMemoriesByDisplayRule } from 'utils/timeline';
import {
  findEarliestTimestamp,
  findLatestTimestamp,
  snapDatetimeToNearest,
} from 'utils/timestamps';
import { getExcludeTrackerEntriesFromTimelyApiRequest } from './development';
import { getIsAppInitialized } from './global';

export const getPixelsPerMinute = (state: IState) => state.timeline.pixelsPerMinute;

const getOperatorMode = createSelector([getHourDraftStatus], (draftStatus): OperatorMode => {
  return draftStatus.exists ? OperatorMode.withHourDraft : OperatorMode.memoryInbox;
});

export const getActualDate = createSelector(
  [getHourDraftDay, getCalendarFilter],
  (draftDay, calendarFilter): string => {
    return draftDay || calendarFilter.date;
  }
);

// Since getTimelineMemories is called from other selectors, like calendar,
// we want to cache the relevant filters to avoid very frequent re-renders
export const getRelevantMemoryFilters = createSelector(
  [getFilter, getActualDate],
  (filter, date): RelevantMemoryFilters => {
    return {
      date,
      expandedEntryIds: filter.expandedEntryIds,
      noiseMinThreshold: filter.noiseMinThreshold,
      importanceMinThreshold: filter.importanceMinThreshold,
      mode: filter.mode,
      searchQuery: filter.searchQuery,
      timeFormat: filter.timeFormat,
    };
  }
);

export const getRelevantMetaMemoryFilters = createSelector(
  [getFilter, getActualDate],
  (filter, date): RelevantMetaMemoryFilters => {
    return {
      date,
      timeFormat: filter.timeFormat,
    };
  }
);

export const getTimelineMetaFromEntries = createSelector(
  [
    getTimelineEntries,
    getRelevantMetaMemoryFilters,
    getHourDraftScrollToTimestamp,
    getPixelsPerMinute,
  ],
  (entries, filter, hourDraftFrom, pixelsPerMinute): ExtractTimelineMetaFromEntries => {
    return createTimelineMetadata(entries, {
      date: filter.date,
      timeFormat: filter.timeFormat,
      hourDraftFrom,
      pixelsPerMinute,
    });
  }
);

export const getIsSelectedDayWithinMemoryHistoryDaysLimit = createSelector(
  [getRelevantMetaMemoryFilters, getAccountFeatures],
  (filter, features): boolean => {
    if (!filter.date) {
      return true;
    }
    const historyDays = features.memory_history_days ?? 0;
    const today = DateTime.now().startOf('day');
    const lastValidHistoryDate = historyDays > 0 ? today.minus({ days: historyDays - 1 }) : today;
    const selectedDateFromFilter = DateTime.fromISO(filter.date).startOf('day');
    return selectedDateFromFilter >= lastValidHistoryDate;
  }
);

export const getPredictedEntryIdsForHourDraftProject = createSelector(
  [getProjectPredictions, getHourDraftProjectMeta],
  (predictions, selectedProjectReference): Array<number> => {
    const predictionProjectId = selectedProjectReference ? selectedProjectReference.id : null;
    const projectPredictions =
      predictionProjectId != null ? predictions[predictionProjectId] : null;

    return predictionProjectId && !!projectPredictions ? projectPredictions.entry_ids : EMPTY_ARRAY;
  }
);

const getSelectedEntryIds = createSelector(
  [getHourDraftEntryIds, getTimelineSelection, getOperatorMode],
  (idsOnHourDraft, idsSelected, operatorMode): Array<number> => {
    return operatorMode === OperatorMode.memoryInbox ? idsSelected.entryIds : idsOnHourDraft;
  }
);

const getMemoryIdToMemoryGroupMap = createSelector([getMemoryGroups], groups =>
  groups.reduce((map: Record<number, SuggestedEntry>, group) => {
    group.entry_ids.forEach(id => {
      map[id] = group;
    });
    return map;
  }, {})
);

/**
 * Enrich entries to become Memories /w rich meta data,
 * such as isLogged, predictions, etc.
 * @todo Look at this for injecting TIC-specific data
 */
export const getTimelineMemories = createSelector(
  [
    getTimelineEntities,
    getTimelineResult,
    getTimelineMetaFromEntries,
    getSelectedEntryIds,
    getEntryIdsAssociatedToHoursOnDraftDay,
    getEntryIdsAssociatedToSuggestionsOnDraftDay,
    getRelevantMemoryFilters,
    getMemoryToProjectRelationsForCalendarDay,
    getPredictedEntryIdsForHourDraftProject,
    getHourDraftProjectMeta,
    getMemoryIdToMemoryGroupMap,
  ],
  (
    entities,
    result,
    timelineMeta,
    selectedEntryIds,
    loggedEntryIds,
    predictedEntryIds,
    filter,
    entryIdToProjectRelations,
    entryIdsPredictedToHourDraftProject,
    currentHourDraftProject,
    groupMap
  ): Array<Memory> => {
    const filteredMemories = result.filter(id => entities[id].date === filter.date);
    const entries = filteredMemories.map(id => entities[id]);

    const memories: Array<Memory> = entries.map(entry => {
      const groupDuration = groupMap[entry.id]?.duration;
      return entryToMemoryFactory(entry, {
        groupDuration,
        selectedEntryIds,
        predictedEntryIds,
        loggedEntryIds,
        timelineMeta,
        filter,
        entryIdToProjectRelations,
        currentHourDraftProject,
        entryIdsPredictedToHourDraftProject,
      });
    });

    return memories.length ? memories : EMPTY_ARRAY;
  }
);

const getTicAndLegacyMemories = lazySelector(
  import('redux/modules/tic/tic.memories.selectors').then(m => m.getTicAndLegacyMemories)
);

const getConstructedTicAndLegacyMemoriesAsMemoryGroup = lazySelector(
  import('redux/modules/tic/tic.memories.selectors').then(
    m => m.getConstructedTicAndLegacyMemoriesAsMemoryGroup
  )
);

const getTicMemoriesInLanes = lazySelector(
  import('redux/modules/tic/tic.memories.selectors').then(m => m.getTicMemoriesInLanes)
);

const getTicMemorySummaryBlocks = lazySelector(
  import('redux/modules/tic/tic.memories.selectors').then(m => m.getTicMemorySummaryBlocks)
);

const getMemoryConfig = lazySelector(
  import('redux/modules/tic/tic.memories.config.selectors').then(m => m.getMemoryConfig)
);

const getHasMemoriesOnTimestamps = createSelector([getHourDraftTimestamps], timestamps => {
  return timestamps.flatMap(timestamp => timestamp.entry_ids).length > 0;
});

const getHasDedicatedMemoryGroupsInHourDraftTimespan = createSelector(
  [getTicMemoriesInLanes, getHourDraftTimestamps],
  (lanes, timestamps) => {
    const earliestTimestamp = findEarliestTimestamp(timestamps);
    const latestTimestamp = findLatestTimestamp(timestamps);
    if (!earliestTimestamp || !latestTimestamp) {
      return false;
    }

    const stickyLanes = lanes?.stickyLanes ?? [];
    const otherLanes = lanes?.lanes ?? [];
    const filteredLanes = otherLanes.filter(lane => lane.dedicated_lane);
    const allDedicatedLanes = [...stickyLanes, ...filteredLanes];
    if (!allDedicatedLanes.length) {
      return false;
    }
    return allDedicatedLanes.some(lane => {
      return lane.sub_lanes.some(subLane => {
        return subLane.some(group => {
          return (
            datetimeTools.isBetween(group.started_at, earliestTimestamp.from, latestTimestamp.to) &&
            datetimeTools.isBetween(group.ended_at, earliestTimestamp.from, latestTimestamp.to)
          );
        });
      });
    });
  }
);

const getHasMemoryGroupsInOtherMemoriesLane = createSelector(
  [getConstructedTicAndLegacyMemoriesAsMemoryGroup, getHourDraftTimestamps],
  (memoryGroups, timestamps) => {
    const earliestTimestamp = findEarliestTimestamp(timestamps);
    const latestTimestamp = findLatestTimestamp(timestamps);
    if (!earliestTimestamp || !latestTimestamp) {
      return false;
    }
    return (memoryGroups ?? []).some(
      group =>
        datetimeTools.isBetween(group.started_at, earliestTimestamp.from, latestTimestamp.to) &&
        datetimeTools.isBetween(group.ended_at, earliestTimestamp.from, latestTimestamp.to)
    );
  }
);

const getHasSummaryBlocksInHourDraftTimespan = createSelector(
  [getTicMemorySummaryBlocks, getHourDraftTimestamps],
  (summaryBlocks, timestamps) => {
    const earliestTimestamp = findEarliestTimestamp(timestamps);
    const latestTimestamp = findLatestTimestamp(timestamps);
    if (!earliestTimestamp || !latestTimestamp) {
      return false;
    }
    return (summaryBlocks?.blocks ?? []).some(block => {
      const start = block.startTime.toISO();
      const end = block.endTime.toISO();
      return (
        datetimeTools.isBetween(start, earliestTimestamp.from, latestTimestamp.to) &&
        datetimeTools.isBetween(end, earliestTimestamp.from, latestTimestamp.to)
      );
    });
  }
);
/**
 * Checks if the timeline has memories within the hour draft timespan (earliest/latest timestamps)
 * Used to determine if we can generate AI notes for the draft, and ensure that we dont show
 * query with an invalid timespan
 */
export const getHasMemoriesWithinHourDraftTimespan = createSelector(
  [
    getHasMemoriesOnTimestamps,
    getHasMemoryGroupsInOtherMemoriesLane,
    getHasDedicatedMemoryGroupsInHourDraftTimespan,
    getHasSummaryBlocksInHourDraftTimespan,
    getMemoryConfig,
  ],
  (
    hasMemoriesOnTimestamps,
    hasMemoryGroupsInOtherMemoriesLane,
    hasDedicatedMemoryGroupsInTimespan,
    hasSummaryBlocksInTimespan,
    memoryConfig
  ) => {
    const useOtherMemoriesLane =
      memoryConfig?.showOtherMemoriesLane && hasMemoryGroupsInOtherMemoriesLane;
    const useDedicatedMemoryGroups = hasDedicatedMemoryGroupsInTimespan && !useOtherMemoriesLane;
    const useSummaryBlocks = hasSummaryBlocksInTimespan && !useDedicatedMemoryGroups;
    return (
      hasMemoriesOnTimestamps ||
      useOtherMemoriesLane ||
      useDedicatedMemoryGroups ||
      useSummaryBlocks
    );
  }
);

export const getFilteredMemories = createSelector(
  [getTimelineMemories, getRelevantMemoryFilters],
  (memories, filter): Array<Memory> => {
    const searchString = filter.searchQuery.toLowerCase();
    if (filter.searchQuery.length) {
      return memories.filter(
        memory => memory.searchString.toLowerCase().indexOf(searchString) !== -1
      );
    }

    return memories.length ? memories : EMPTY_ARRAY;
  }
);

/**
 * Return what types of memories should pass the filter check.
 * This varies based on filter.mode (timeline & groups) by default, and is overwritten by user preferences.
 */
export const getDisplayRules = createSelector(
  [getFilter, getOperatorMode, getTimelineStyle],
  (filter, operatorMode, timelineStyle): DisplayRules => {
    const generateDataFor =
      operatorMode === OperatorMode.memoryInbox && filter.mode === 'group'
        ? OperatorMode.memoryInbox
        : filter.mode;

    const rules = filter.mode === 'group' ? filter.displayGroupRules : filter.displayTimelineRules;
    const showUnlogged = rules.includes('other');
    const hasRelevantMemoriesHidden = generateDataFor !== OperatorMode.memoryInbox && !showUnlogged;

    return {
      hasRelevantMemoriesHidden,
      showLogged: rules.includes('logged'),
      showPredicted: rules.includes('logged'), // 'predicted' grouped with logged atm
      showSpam: rules.includes('spam'),
      showUnlogged,
      timelineStyle,
    };
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export interface SelectionSummary {
  duration: Duration;
  numSelected: number;
  entryIds: Array<number>;
}
export interface GetMemoriesForViewProps {
  groups: MemoriesGroupedByStatus | null | undefined;
  timeline: Array<Array<Memory>>;
  memoryInbox: Array<MemoryGroup>;
  isEmpty: boolean;
  hasSelectedAllMemories: boolean;
  operatorMode: OperatorMode;
  selectionSummary: SelectionSummary;
}

const getSelectedMemories = createSelector([getFilteredMemories], memories => {
  const selectedMemories = memories.filter(memory => memory.isChecked);
  return selectedMemories.length ? selectedMemories : EMPTY_ARRAY;
});

const getSelectionSummary = createSelector([getSelectedMemories], selectedMemories => {
  return {
    duration: combinedDurationOfMemories(selectedMemories),
    numSelected: selectedMemories.length,
    entryIds: selectedMemories.map(({ id }) => id),
  };
});

export const getMemoriesForViewMode = createSelector(
  [
    getFilteredMemories,
    getFilter,
    getHourDraftProjectId,
    getMemoryGroupsWithProjects,
    getOperatorMode,
    getDisplayRules,
    getSelectionSummary,
    getHasEnabledTic,
  ],
  (
    memories,
    filter,
    activeProjectId,
    memoryGroups,
    operatorMode,
    displayRules,
    selectionSummary,
    hasExperimentalTimestampsAndMemories
  ): GetMemoriesForViewProps => {
    let groups: MemoriesGroupedByStatus | undefined;
    let timeline: Array<Array<Memory>> = [];
    let memoryInbox: Array<MemoryGroup> = [];
    const generateDataFor =
      operatorMode === OperatorMode.memoryInbox && filter.mode === 'group'
        ? OperatorMode.memoryInbox
        : filter.mode;

    switch (generateDataFor) {
      case 'timeline': {
        const filteredMemories = filterMemoriesByDisplayRule(memories, { displayRules });
        // @todo Use memory group's importance to generate these lanes
        timeline = generateMemoryTimelineFactory(filteredMemories, {
          minSpaceBetweenMemoriesInHours: 5 / 60,
          minMemoryDurationInHours: 5 / 60,
          sortBy: filter.timelineSort,
        });
        break;
      }
      case 'memoryInbox': {
        const displayRulesOverride = {
          ...displayRules,
          showLogged: hasExperimentalTimestampsAndMemories,
          showPredicted: false,
        };

        const baseMemories = hasExperimentalTimestampsAndMemories
          ? memories
          : memories.filter(memory => !memory.isChecked);

        const filteredMemories = filterMemoriesByDisplayRule(baseMemories, {
          displayRules: displayRulesOverride,
        });

        memoryInbox = groupMemoriesFactory(memoryGroups, filteredMemories, {
          importanceMinThreshold: filter.importanceMinThreshold,
          expandGroupRefs: filter.expandGroupRefs,
          expandedEntryIds: filter.expandedEntryIds,
          activeProjectId,
          operatorMode,
        });
        break;
      }
      default: {
        const displayRulesOverride = {
          ...displayRules,
          showLogged: hasExperimentalTimestampsAndMemories || displayRules.showLogged,
        };

        const filteredMemories = filterMemoriesByDisplayRule(memories, {
          displayRules: displayRulesOverride,
        });

        const filteredGrouped = groupMemoriesFactory(memoryGroups, filteredMemories, {
          importanceMinThreshold: filter.importanceMinThreshold,
          expandGroupRefs: filter.expandGroupRefs,
          expandedEntryIds: filter.expandedEntryIds,
          activeProjectId,
          operatorMode,
        });

        // Split by status
        groups = splitMemoryGroupsByStatus(filteredGrouped);
        break;
      }
    }

    const hasMemories =
      generateDataFor === 'timeline'
        ? !!memories.length
        : generateDataFor === 'memoryInbox'
          ? memoryInbox.length > 0
          : groups && groups.totals.count > 0;

    const hasSelectedAllMemories =
      memories.length > 0 &&
      memories.every(memory => memory.isLogged || memory.isChecked || memory.isPredicted);

    return {
      groups,
      timeline: timeline.length ? timeline : EMPTY_ARRAY,
      memoryInbox: memoryInbox.length ? memoryInbox : EMPTY_ARRAY,
      isEmpty: !hasMemories,
      hasSelectedAllMemories,
      operatorMode,
      selectionSummary,
    };
  }
);

export const getHintsForMemorySidebar = createSelector(
  [
    getHints,
    getHourDraftStatus,
    getAiIgnoredMemories,
    state => getUserPropertyAsBoolean(state, HAS_SEEN_AI_IGNORED_MEMORIES_HINT),
    state => getUserPropertyAsNumber(state, HAS_IGNORED_MEMORY),
    getDisplayRules,
  ],
  (
    hints,
    draftStatus,
    aiIgnoredMemories,
    hasSeenAiIgnoredMemoriesHint,
    hasIgnoredMemoryHint,
    displayRules
  ) => {
    const isSuggestion = !!draftStatus?.isSuggestionInReview;
    const isCreatingNew = !!draftStatus?.isCreatingNew;
    const hasEntries = !!draftStatus?.hasEntries;

    const showHints: Array<OnboardingHint> = [];

    if (hints[HAS_SEEN_UNLINKED_MEMORIES] && isCreatingNew && !isSuggestion && !hasEntries) {
      showHints.push(HAS_SEEN_UNLINKED_MEMORIES);
    }

    if (!!aiIgnoredMemories.length && !hasSeenAiIgnoredMemoriesHint) {
      showHints.push(HAS_SEEN_AI_IGNORED_MEMORIES);
    }

    if (!displayRules.showSpam && hasIgnoredMemoryHint === 1) {
      showHints.push(HAS_IGNORED_MEMORY);
    }

    return showHints.length ? showHints : EMPTY_ARRAY;
  }
);

// todo Filter memories on calendar date -- or getFilteredMemorySummaries?

const getMemorySidebarUi = createSelector(
  [
    getDisplayRules,
    getMemoriesForViewMode,
    getTimelineMetaFromEntries,
    getScrollPosition,
    getHourDraftStatus,
    getTimelineXhrStatusState,
    getIsUserInExperimentalFeatureGroup,
    getIsSelectedDayWithinMemoryHistoryDaysLimit,
    getFilter,
    getHasConnectedMemorySource,
    getIsAppInitialized,
    state => getUserPropertyAsBoolean(state, HIDE_SHORT_DURATION_MEMORY_TITLES),
    getHasTicWorkspaceSupport,
    getHasEnabledTic,
    getIsSubscriptionExpiredOrTrialEnded,
    getAiIgnoredMemories,
    getTicViewDemoMode,
  ],
  (
    displayRules,
    memoriesForView,
    meta,
    preservedScrollPosition,
    draftStatus,
    timelineXhrStatusState,
    showDebugInfo,
    isSelectedDayWithinMemoryHistoryDaysLimit,
    filter,
    hasAppConnected,
    isAppInitialized,
    hideShortDurationMemoryTitles,
    hasTicWorkspaceSupport,
    hasEnabledTic,
    isSubscriptionExpiredOrTrialEnded,
    aiIgnoredMemories,
    ticViewDemoMode
  ) => {
    const { isEmpty, operatorMode, hasSelectedAllMemories } = memoriesForView;

    const isMemoriesOnThisDayFeatureLocked = !isSelectedDayWithinMemoryHistoryDaysLimit;

    const isLoading =
      timelineXhrStatusState.isLoading ||
      !timelineXhrStatusState.hasInitialized ||
      !isAppInitialized;
    const showEmptyState =
      isEmpty && isAppInitialized && !isMemoriesOnThisDayFeatureLocked && !ticViewDemoMode.enabled;

    // Determine which empty state is the most likely
    const emptyStateType = memoryEmptyStateFactory({
      hasAppConnected,
      isEmpty,
      hasSelectedAllMemories,
      date: meta.date,
      // `totalMemories` is incorrect when only TIC memories exist,
      // but since isEmpty _does_ check for TIC memories, we fake force 1 here
      // This is INTENTIONALLY HACKY, because TIC will make huge parts of these
      // selectors obsolete, so we don't want to spend time on this.
      totalMemories: meta.totalMemories ? meta.totalMemories : isEmpty ? 0 : 1,
      isFiltering: displayRules.hasRelevantMemoriesHidden || filter.searchQuery.length > 0,
    });

    return {
      displayRules,
      operatorMode,
      showDebugInfo,
      isEmpty,
      isLoading,
      isAppInitialized,
      showEmptyState,
      toolbarMode: operatorMode === OperatorMode.memoryInbox ? 'write' : 'read',
      emptyStateType,
      showPlaceholderMemories: isLoading && isEmpty && !isMemoriesOnThisDayFeatureLocked,
      hasHourDraft: draftStatus.exists,
      isDisabled: draftStatus.isLocked || isSubscriptionExpiredOrTrialEnded,
      isSubscriptionExpiredOrTrialEnded,
      showUpgradeState: isMemoriesOnThisDayFeatureLocked,
      preservedScrollPosition,
      isToday: isSameDay(DateTime.now().toISODate(), meta.date),
      hideShortDurationMemoryTitles,
      aiIgnoredMemoriesCount: aiIgnoredMemories.length,
      hasTicWorkspaceSupport,
      hasEnabledTic,
    };
  }
);

export type MemorySidebarUIProps = ReturnType<typeof getMemorySidebarUi>;

export const getMemorySidebarProps = createSelector(
  [
    getFilter,
    getTimelineMetaFromEntries,
    getLastPusherUpdate,
    getMemoriesForViewMode,
    getHintsForMemorySidebar,
    getMemorySidebarUi,
    getToolbarApps,
    getTimelineStyle,
    getPixelsPerMinute,
    getExcludeTrackerEntriesFromTimelyApiRequest,
    getUseTicAPI,
  ],
  (
    filter,
    meta,
    lastPusherUpdate,
    memoriesForView,
    hints,
    ui,
    toolbarApps,
    timelineStyle,
    pixelsPerMinute,
    excludeTrackerEntries,
    useTicAPI
  ) => {
    const { groups, timeline, memoryInbox, selectionSummary } = memoriesForView;
    return {
      groups,
      timeline,
      memoryInbox,
      filter,
      meta,
      selectionSummary,
      hints,
      ui,
      toolbarApps,
      hasExperimentalStyle: ui.hasEnabledTic,
      timelineStyle,
      pixelsPerMinute,
      // Refetch when pusher invalidates our data (today only), or date changes
      // See timeline.actions::initializeMemory
      XhrCacheBuster: JSON.stringify({
        useTicAPI,
        date: meta.date,
        relevantPush: lastPusherUpdate,
        excludeTrackerEntries,
      }),
    };
  }
);

/**
 * @todo Pretty sure we can avoid some of the memory-related selectors to speed this up.
 * Further context: https://github.com/Timely/Frontend/pull/1828#discussion_r399472422
 */
export const getDraftMemoryGroups = createSelector(
  [getMemoryGroupsWithProjects, getTimelineMemories, getFilter],
  (memoryGroups, memories, timelineFilter) => {
    return groupMemoriesFactory(memoryGroups, memories, {
      importanceMinThreshold: timelineFilter.importanceMinThreshold,
      expandGroupRefs: timelineFilter.expandGroupRefs,
      expandedEntryIds: timelineFilter.expandedEntryIds,
      activeProjectId: null,
      operatorMode: OperatorMode.withHourDraft,
    }).filter(item => item.isPartlyChecked || item.isChecked);
  }
);

export const getDraftMemorySectionProps = createSelector([getDraftMemoryGroups], memoryGroups => {
  return { memoryGroups };
});

export const getIndirectDraftEntries = createSelector(
  [
    getTicAndLegacyMemories,
    getRelevantMemoryFilters,
    getSelectedEntryIds,
    getHourDraftTimestamps,
    getTimestampSnappingMinutes,
  ],
  (entries, filters, selectedIds, timestamps, snappingMinutes) => {
    const cache = new Map<string, DateTime<true>>();
    const cachedFromISO = (datetime: string): DateTime<true> => {
      let value = cache.get(datetime);
      if (!value) {
        value = fromISO(datetime);
        cache.set(datetime, value);
      }
      return value;
    };

    return (entries ?? []).filter(entry => {
      if (selectedIds.includes(entry.id)) {
        return true;
      }

      // Account for timestamp snapping when checking for timestamp overlap with each memory
      // Do this by overriding memory's start and end times to the nearest snapping minute,
      // but only in one direction (making the memory smaller, and thus the check more forgiving)
      const entryFrom = DateTime.max(
        cachedFromISO(entry.started_at),
        snapDatetimeToNearest(cachedFromISO(entry.started_at), snappingMinutes)
      );
      const entryTo = DateTime.min(
        cachedFromISO(entry.ended_at),
        snapDatetimeToNearest(cachedFromISO(entry.ended_at), snappingMinutes)
      );
      if (
        timestamps.some(
          timestamp =>
            cachedFromISO(timestamp.from) <= entryFrom && entryTo <= cachedFromISO(timestamp.to)
        )
      ) {
        return true;
      }
      return false;
    });
  }
);
